/**
 * ReportCard Component
 * Mobile card display for power reports
 * Used by ActiveReportsTab and RecentReportsTab
 */

import {
  EyeIcon,
  TrashIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentDuplicateIcon,
  NoSymbolIcon
} from '@heroicons/react/24/outline';
import { formatCurrency, formatDate, getStatusBadgeClasses } from '../utils/formatters';

const ReportCard = ({
  report,
  onView,
  onDelete,
  onVoid,
  showDelete = true,
  showPaymentMethod = false,
  showNetBuf = true,
  showVoid = false,
  canVoid = false,
  voidTimeRemaining = null,
}) => {
  const status = report.status || 'Draft';

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-md p-4 mb-3 active:bg-gray-50 dark:active:bg-gray-700 transition-colors cursor-pointer"
      onClick={() => onView(report)}
    >
      {/* Header Row */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {report.reportNumber || 'New Report'}
            </h3>
            <span className={getStatusBadgeClasses(status)}>
              {status}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
            <CalendarIcon className="h-3.5 w-3.5" />
            {formatDate(report.reportDate || report.dateCreated)}
          </p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); onView(report); }}
            className="p-2.5 sm:p-2 text-primary dark:text-primary-light hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-full transition-colors"
            aria-label="View details"
          >
            <EyeIcon className="h-5 w-5" />
          </button>
          {showDelete && onDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(report); }}
              className="p-2.5 sm:p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"
              aria-label="Delete report"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
          {showVoid && onVoid && canVoid && (
            <button
              onClick={(e) => { e.stopPropagation(); onVoid(report); }}
              className="p-2.5 sm:p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-full transition-colors"
              aria-label="Void ACH payment"
              title={voidTimeRemaining ? `Void ACH payment (${voidTimeRemaining.formatted} until NACHA cutoff)` : 'Void ACH payment'}
            >
              <NoSymbolIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Data Grid */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        {/* Bond Count */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 flex items-center gap-1">
            <DocumentDuplicateIcon className="h-3.5 w-3.5" />
            Bonds
          </p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {report.powerCount || report.numberOfPowers || 0}
          </p>
        </div>

        {/* Liability */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5 flex items-center gap-1">
            <CurrencyDollarIcon className="h-3.5 w-3.5" />
            Liability
          </p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ${formatCurrency(report.totalBondAmount || 0)}
          </p>
        </div>

        {/* Premium */}
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Premium</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            ${formatCurrency(report.totalPremium || 0)}
          </p>
        </div>

        {/* NET / BUF — hidden on submitted reports, where the PDF is the record */}
        {showNetBuf && (
          <>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">NET</p>
              <p className="text-sm font-medium text-primary dark:text-primary-light">
                ${formatCurrency(report.totalNet || 0)}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">BUF</p>
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                ${formatCurrency(report.totalBuf || 0)}
              </p>
            </div>
          </>
        )}

        {/* Payment Method (for recent reports) */}
        {showPaymentMethod && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Payment</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {report.electronicPayment ? 'ACH' : 'Check'}
            </p>
          </div>
        )}

        {/* Batch # (for recent reports) */}
        {showPaymentMethod && (
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Batch #</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {report.batchId || '—'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportCard;
